import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

import GlassCard from '../../ui/v2/GlassCard';
import GlassButton from '../../ui/v2/GlassButton';
import { RiRefreshLine, RiArrowRightLine, RiEyeLine, RiTimeLine, RiCloseLine, RiGridLine } from 'react-icons/ri';

interface RecentActivityProps {
    interviews: any[];
    onReuse: (interview: any) => void;
    loading?: boolean;
}

function ScoreBadge({ score, isPending, isCancelled }: { score: number; isPending: boolean; isCancelled: boolean }) {
    const getColor = (s: number) => s >= 85 ? 'text-green-400' : s >= 70 ? 'text-yellow-400' : 'text-red-400';
    const getBg = (s: number) => s >= 85 ? 'bg-green-500/20' : s >= 70 ? 'bg-yellow-500/20' : 'bg-red-500/20';

    return (
        <div className={`w-11 h-11 rounded-full flex items-center justify-center border border-white/10 shrink-0
            ${isPending ? 'bg-blue-500/10 border-blue-500/20' : isCancelled ? 'bg-red-500/10 border-red-500/20' : getBg(score)}`}>
            {isPending ? (
                <RiTimeLine className="text-lg text-blue-400" />
            ) : isCancelled ? (
                <RiCloseLine className="text-lg text-red-500" />
            ) : (
                <span className={`text-xs font-bold ${getColor(score)}`}>{score > 0 ? score.toFixed(0) : '0'}</span>
            )}
        </div>
    );
}

function InterviewCard({ interview, onReuse, onClick }: { interview: any; onReuse: (i: any) => void; onClick: () => void }) {
    const id = interview.id || interview._id;
    const jobTitle = interview.job_title || interview.setup?.setup_context?.job_title || interview.setup?.job_title || 'Untitled Interview';
    const company = interview.company || interview.setup?.setup_context?.company?.name || interview.setup?.company?.name || 'Company';
    const date = interview.created_at || interview.date;
    const score = interview.overall_score || 0;
    const status = interview.status;
    const isSimulation = interview.type === 'simulation';

    const isCompleted = status === 'completed';
    const isPending = status === 'in_progress' || status === 'active' || status === 'questions_ready' || status === 'uploaded';
    const isCancelled = status === 'cancelled';
    const isClickable = isCompleted || isPending;

    return (
        <div
            className={`flex flex-col gap-3 p-4 rounded-xl border border-white/[0.07] transition-all duration-200
                ${isClickable ? 'bg-white/[0.03] hover:bg-white/[0.07] hover:border-white/[0.12] cursor-pointer' : 'bg-white/[0.02] opacity-50 cursor-not-allowed'}`}
            onClick={() => { if (isClickable) onClick(); }}
        >
            {/* Top row */}
            <div className="flex items-start justify-between gap-2">
                <ScoreBadge score={score} isPending={isPending} isCancelled={isCancelled} />
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                    {isSimulation && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400">AI Sim</span>
                    )}
                    {isCancelled && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400">Cancelled</span>
                    )}
                    {isPending && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400">In Progress</span>
                    )}
                </div>
            </div>

            {/* Info */}
            <div className="flex-1">
                <h4 className="font-semibold text-white text-sm line-clamp-2 leading-snug mb-1">{jobTitle}</h4>
                <p className="text-xs text-zinc-500 truncate">{company}</p>
                <p className="text-xs text-zinc-600 mt-0.5">{date ? new Date(date).toLocaleDateString() : 'N/A'}</p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 pt-1 border-t border-white/[0.05]">
                <GlassButton
                    variant="outline"
                    size="sm"
                    className="h-8 px-2 text-xs flex-1"
                    title="Reuse settings"
                    onClick={(e) => { e.stopPropagation(); onReuse(interview); }}
                >
                    <RiRefreshLine className="text-base mr-1" /> Reuse
                </GlassButton>
                {isPending && (
                    <GlassButton
                        variant="secondary"
                        size="sm"
                        className="h-8 px-2 text-xs flex-1 bg-white/10 hover:bg-white/20 border-white/10"
                        onClick={(e) => { e.stopPropagation(); onClick(); }}
                    >
                        <RiArrowRightLine className="mr-1 text-base" /> Continue
                    </GlassButton>
                )}
                {isCompleted && (
                    <GlassButton
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-xs flex-1 text-zinc-300 hover:text-white hover:bg-white/5"
                        onClick={(e) => { e.stopPropagation(); onClick(); }}
                    >
                        <RiEyeLine className="mr-1 text-base" /> View
                    </GlassButton>
                )}
            </div>
        </div>
    );
}

function GridModal({ interviews, onReuse, onClose }: { interviews: any[]; onReuse: (i: any) => void; onClose: () => void }) {
    const navigate = useNavigate();

    const getNav = (interview: any) => {
        const id = interview.id || interview._id;
        const status = interview.status;
        const isSimulation = interview.type === 'simulation';
        const isCompleted = status === 'completed';
        const isPending = status === 'in_progress' || status === 'active' || status === 'questions_ready' || status === 'uploaded';

        if (isCompleted) return isSimulation ? `/simulation-result/${id}` : `/interview-detail/${id}`;
        if (isPending) return isSimulation ? `/simulation/${id}` : `/video-interview/${id}`;
        return null;
    };

    return createPortal(
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex flex-col"
                onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            >
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.25 }}
                    className="flex-1 flex flex-col bg-[#0a0a0a] border-t border-white/[0.08] mt-12 rounded-t-2xl overflow-hidden"
                >
                    {/* Modal Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06] flex-shrink-0">
                        <div className="flex items-center gap-3">
                            <RiGridLine className="text-zinc-400 text-xl" />
                            <h2 className="text-lg font-bold text-white">Interview History</h2>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-zinc-400">{interviews.length} total</span>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                        >
                            <RiCloseLine className="text-lg" />
                        </button>
                    </div>

                    {/* Grid */}
                    <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                        {interviews.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {interviews.map((interview) => {
                                    const id = interview.id || interview._id;
                                    const nav = getNav(interview);
                                    return (
                                        <motion.div
                                            key={id}
                                            initial={{ opacity: 0, scale: 0.97 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <InterviewCard
                                                interview={interview}
                                                onReuse={onReuse}
                                                onClick={() => { if (nav) { onClose(); navigate(nav, { state: { interview } }); } }}
                                            />
                                        </motion.div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full text-zinc-500 gap-3">
                                <RiRefreshLine className="text-3xl opacity-30" />
                                <p>No interviews yet. Start one to see history here.</p>
                            </div>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>,
        document.body
    );
}

export default function RecentActivity({ interviews, onReuse, loading = false }: RecentActivityProps) {
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);

    const previewInterviews = interviews.slice(0, 5);
    const showSkeleton = loading && interviews.length === 0;

    const getScoreColor = (score: number) => {
        if (score >= 85) return 'text-green-400';
        if (score >= 70) return 'text-yellow-400';
        return 'text-red-400';
    };

    const getScoreBgColor = (score: number) => {
        if (score >= 85) return 'bg-green-500/20';
        if (score >= 70) return 'bg-yellow-500/20';
        return 'bg-red-500/20';
    };

    return (
        <>
            <GlassCard className="flex flex-col">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-white">Interview History</h2>
                    {interviews.length > 0 && (
                        <button
                            onClick={() => setShowModal(true)}
                            className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
                        >
                            <RiGridLine className="text-base" />
                            View All ({interviews.length})
                        </button>
                    )}
                </div>

                {showSkeleton ? (
                    <div className="space-y-3">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-[72px] rounded-xl bg-white/[0.03] animate-pulse" />
                        ))}
                    </div>
                ) : interviews.length > 0 ? (
                    <div className="space-y-3">
                        {previewInterviews.map((interview) => {
                            const id = interview.id || interview._id;
                            const jobTitle = interview.job_title || interview.setup?.setup_context?.job_title || interview.setup?.job_title || 'Untitled Interview';
                            const company = interview.company || interview.setup?.setup_context?.company?.name || interview.setup?.company?.name || 'Company';
                            const date = interview.created_at || interview.date;
                            const score = interview.overall_score || 0;
                            const status = interview.status;
                            const isSimulation = interview.type === 'simulation';

                            const isCompleted = status === 'completed';
                            const isPending = status === 'in_progress' || status === 'active' || status === 'questions_ready' || status === 'uploaded';
                            const isCancelled = status === 'cancelled';

                            const handleCardClick = () => {
                                if (isCompleted) {
                                    if (isSimulation) navigate(`/simulation-result/${id}`);
                                    else navigate(`/interview-detail/${id}`, { state: { interview } });
                                } else if (isPending) {
                                    if (isSimulation) navigate(`/simulation/${id}`);
                                    else navigate(`/video-interview/${id}`);
                                }
                            };

                            return (
                                <div
                                    key={id}
                                    className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl border border-white/5 gap-4 transition-all duration-200
                                        ${isCompleted || isPending ? 'bg-white/[0.03] hover:bg-white/[0.06] cursor-pointer hover:border-white/10' : 'bg-white/[0.02] opacity-60 cursor-not-allowed'}`}
                                    onClick={() => { if (!isCancelled) handleCardClick(); }}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-full flex items-center justify-center border border-white/10 shrink-0
                                            ${isPending ? 'bg-blue-500/10 border-blue-500/20' : isCancelled ? 'bg-red-500/10 border-red-500/20' : getScoreBgColor(score)}`}>
                                            {isPending ? (
                                                <RiTimeLine className="text-xl text-blue-400" />
                                            ) : isCancelled ? (
                                                <RiCloseLine className="text-xl text-red-500" />
                                            ) : (
                                                <span className={`font-bold ${getScoreColor(score)}`}>{score > 0 ? score.toFixed(1) : '0'}</span>
                                            )}
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-white text-lg line-clamp-1">{jobTitle}</h4>
                                            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-400">
                                                <span>{company}</span>
                                                <span className="hidden sm:inline">•</span>
                                                <span>{date ? new Date(date).toLocaleDateString() : 'N/A'}</span>
                                                {isSimulation && (
                                                    <>
                                                        <span className="hidden sm:inline">•</span>
                                                        <span className="text-purple-400 text-xs px-2 py-0.5 rounded-full bg-purple-500/10 border border-purple-500/20">AI Sim</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3 self-end sm:self-auto">
                                        {isCancelled && (
                                            <div className="px-3 py-1 rounded-full text-xs font-medium border bg-red-500/10 text-red-400 border-red-500/20">
                                                Cancelled
                                            </div>
                                        )}
                                        <GlassButton
                                            variant="outline"
                                            size="sm"
                                            className="h-9 px-2.5 text-xs hover:border-white/40"
                                            title="Reuse Interview Settings"
                                            onClick={(e) => { e.stopPropagation(); onReuse(interview); }}
                                        >
                                            <RiRefreshLine className="text-lg" />
                                        </GlassButton>
                                        {isPending && (
                                            <GlassButton
                                                variant="secondary"
                                                size="sm"
                                                className="h-9 px-4 text-xs bg-white/10 hover:bg-white/20 border-white/10"
                                                onClick={(e) => { e.stopPropagation(); handleCardClick(); }}
                                            >
                                                <RiArrowRightLine className="mr-1.5 text-base" />
                                                Continue
                                            </GlassButton>
                                        )}
                                        {isCompleted && (
                                            <GlassButton
                                                variant="ghost"
                                                size="sm"
                                                className="h-9 px-4 text-xs text-gray-300 hover:text-white hover:bg-white/5"
                                                onClick={(e) => { e.stopPropagation(); handleCardClick(); }}
                                            >
                                                View
                                                <RiEyeLine className="ml-1.5 text-base" />
                                            </GlassButton>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {interviews.length > 5 && (
                            <button
                                onClick={() => setShowModal(true)}
                                className="w-full py-3 text-sm text-zinc-500 hover:text-white border border-white/[0.05] hover:border-white/[0.1] rounded-xl transition-colors"
                            >
                                + {interviews.length - 5} more — View all in grid
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-8 text-gray-500">
                        <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                            <RiRefreshLine className="text-2xl opacity-50" />
                        </div>
                        <p>No history yet. Start a new interview to begin practicing!</p>
                    </div>
                )}
            </GlassCard>

            {showModal && (
                <GridModal
                    interviews={interviews}
                    onReuse={onReuse}
                    onClose={() => setShowModal(false)}
                />
            )}
        </>
    );
}
